import type { CSSProperties } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { CONTACT_CHANNELS, CONTACT_HERO, CONTACT_TOPICS } from '../../data/contact';
import { SiteButton } from '../site/SiteButton';
import { ContactChannelCard } from './ContactChannelCard';
import { ContactForm } from './ContactForm';

export function ContactShowcase() {
  const { ref: mainRef, visible: mainVisible } = useScrollReveal<HTMLElement>(0.12);
  const { ref: topicsRef, visible: topicsVisible } = useScrollReveal<HTMLElement>(0.12);
  const { ref: ctaRef, visible: ctaVisible } = useScrollReveal<HTMLElement>(0.18);

  return (
    <div className="contact-showcase">
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero__inner">
            <span className="contact-hero__tag">{CONTACT_HERO.tag}</span>
            <h1 className="contact-hero__title">{CONTACT_HERO.title}</h1>
            <p className="contact-hero__subtitle paragraph-02 text-dark-grey-two">
              {CONTACT_HERO.subtitle}
            </p>
            <div className="contact-hero__chips">
              {CONTACT_HERO.chips.map((chip) =>
                'href' in chip ? (
                  <a
                    key={chip.label}
                    href={chip.href}
                    className="contact-hero__chip"
                    {...(chip.href.startsWith('http')
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {chip.label}
                  </a>
                ) : (
                  <span key={chip.label} className="contact-hero__chip">
                    {chip.label}
                  </span>
                )
              )}
            </div>
          </div>

          <div className="contact-hero__orbit" aria-hidden>
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="contact-hero__orbit-dot"
                style={{ '--orbit-i': i } as CSSProperties}
              />
            ))}
          </div>
        </div>
      </section>

      <section
        ref={mainRef}
        className={`contact-main${mainVisible ? ' is-visible' : ''}`}
      >
        <div className="container">
          <div className="contact-main__grid">
            <ContactForm />
            <div className="contact-main__channels">
              {CONTACT_CHANNELS.map((channel, index) => (
                <ContactChannelCard key={channel.id} channel={channel} index={index} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        ref={topicsRef}
        className={`contact-topics${topicsVisible ? ' is-visible' : ''}`}
      >
        <div className="container">
          <div className="contact-topics__header reveal-item">
            <span className="contact-topics__tag">How we can help</span>
            <h2>Pick a topic, start a conversation</h2>
            <p className="paragraph-02 text-dark-grey-two">
              Not sure where to begin? Here are the most common reasons people reach out to Hello Gwiji.
            </p>
          </div>
          <div className="contact-topics__grid">
            {CONTACT_TOPICS.map((topic, index) => (
              <article
                key={topic.title}
                className="contact-topic-card reveal-item"
                style={{ transitionDelay: `${index * 0.08}s` }}
              >
                <span className="contact-topic-card__num">0{index + 1}</span>
                <h3>{topic.title}</h3>
                <p>{topic.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section ref={ctaRef} className={`contact-cta${ctaVisible ? ' is-visible' : ''}`}>
        <div className="container">
          <div className="contact-cta__card reveal-item">
            <h2>See Hello Gwiji on every screen</h2>
            <p className="paragraph-02 text-dark-grey-two">
              MacBook, iPad, iOS, and Android, explore how the platform looks before you reach out.
            </p>
            <SiteButton href="/application" variant="cta">
              View the application
            </SiteButton>
          </div>
        </div>
      </section>
    </div>
  );
}
